import { useContext, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Bell } from 'lucide-react'
import { Form, FormCheck } from 'react-bootstrap'
import { AuthContext } from '@/context/auth-context'
import Card from '@/components/common/Card'
import DataTable from '@/components/common/DataTable'
import {
    GET_NOTIFICATIONS,
    GET_UNREAD_NOTIFICATIONS_COUNT,
    MARK_ALL_NOTIFICATIONS_READ,
    MARK_NOTIFICATIONS_READ,
} from '@/graphql/notification-queries'
import { GET_CAMPAIGNS_FOR_PICKER } from '@/graphql/campaign-queries'
import useNotificationsStore from '@/store/notificationsStore'
import { CubeLoader } from '@/components/ui/cube-loader'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { checkIconSvg } from '@/components/icons/new'

const TABS = [
    { value: 'all', label: 'All', types: null },
    {
        value: 'system',
        label: 'System',
        types: [
            'CAMPAIGN_PAUSED',
            'CAMPAIGN_ACTIVATED',
            'CAMPAIGN_CREATED',
            'CAMPAIGN_FAILED',
            'CAMPAIGN_MODIFIED',
            'AD_REJECTED',
            'AD_APPROVED',
            'AD_REMOVED',
            'PLATFORM_CONNECTED',
            'PLATFORM_DISCONNECTED',
            'BUDGET_PROPOSAL',
            'BUDGET_APPLIED',
            'BUDGET_ATTENTION',
        ],
    },
    {
        value: 'billing',
        label: 'Billing',
        types: ['PAYMENT_SUCCEEDED', 'PAYMENT_FAILED', 'SUBSCRIPTION_CREATED', 'SUBSCRIPTION_MODIFIED', 'SUBSCRIPTION_CANCELED'],
    },
    { value: 'limits', label: 'Limits', types: ['CREATIVES_LIMIT_WARNING'] },
]

const TYPE_META = {
    CAMPAIGN_PAUSED: { title: 'Campaign paused', category: 'System' },
    CAMPAIGN_ACTIVATED: { title: 'Campaign started', category: 'System' },
    CAMPAIGN_CREATED: { title: 'Campaign created', category: 'System' },
    CAMPAIGN_FAILED: { title: 'Campaign failed to publish', category: 'System' },
    CAMPAIGN_MODIFIED: { title: 'Campaign updated', category: 'System' },
    AD_REJECTED: { title: 'Ad was rejected', category: 'System' },
    AD_APPROVED: { title: 'Ad approved', category: 'System' },
    AD_REMOVED: { title: 'Ad removed', category: 'System' },
    PLATFORM_CONNECTED: { title: 'Provider connected', category: 'System' },
    PLATFORM_DISCONNECTED: { title: 'Provider disconnected', category: 'System' },
    PAYMENT_SUCCEEDED: { title: 'Payment processed successfully', category: 'Billing' },
    PAYMENT_FAILED: { title: 'Payment failed', category: 'Billing' },
    SUBSCRIPTION_CREATED: { title: 'Subscription created', category: 'Billing' },
    SUBSCRIPTION_MODIFIED: { title: 'Subscription updated', category: 'Billing' },
    SUBSCRIPTION_CANCELED: { title: 'Subscription canceled', category: 'Billing' },
    CREATIVES_LIMIT_WARNING: { title: 'Ads limit reached', category: 'Limits' },
    BUDGET_PROPOSAL: { title: 'Budget change suggested', category: 'System' },
    BUDGET_APPLIED: { title: 'Budget moved between ad sets', category: 'System' },
    BUDGET_ATTENTION: { title: 'Budget change needs attention', category: 'System' },
    PLATFORM_CONNECTED: { title: 'Platform connected', category: 'System' },
    PLATFORM_DISCONNECTED: { title: 'Platform disconnected', category: 'System' },
}

const CATEGORY_BADGE_COLOR = {
    System: 'var(--notif-system, #263e57)',
    Billing: 'var(--notif-billing, #38bc9b)',
    Limits: 'var(--notif-limits, #b6581e)',
}

const STATUS_BADGE_COLOR = {
    unread: 'var(--notif-unread, #2450bf)',
    read: 'var(--notif-read, #263e57)',
}

// DataTable renders columns as raw HTML strings, so any server-provided text must be escaped here.
const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

const formatDate = (isoString) => {
    if (!isoString) return ''
    return new Date(isoString).toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

// Fetched in one batch and paginated client-side by the shared DataTable, matching the Licenses List page.
const NOTIFICATIONS_FETCH_LIMIT = 1000

const Notifications = () => {
    const { user } = useContext(AuthContext)
    const setUnreadCount = useNotificationsStore((s) => s.setUnreadCount)
    const decrementUnreadBy = useNotificationsStore((s) => s.decrementUnreadBy)
    const resetUnread = useNotificationsStore((s) => s.resetUnread)

    const [activeTab, setActiveTab] = useState('all')
    const [unreadOnly, setUnreadOnly] = useState(false)
    const [campaignId, setCampaignId] = useState('')

    const activeTabConfig = useMemo(() => TABS.find((t) => t.value === activeTab), [activeTab])

    const { data: campaignsData } = useQuery(GET_CAMPAIGNS_FOR_PICKER, {
        fetchPolicy: 'cache-and-network',
    })
    const campaigns = campaignsData?.campaigns ?? []

    const queryVariables = {
        userId: user?.id,
        limit: NOTIFICATIONS_FETCH_LIMIT,
        offset: 0,
        types: activeTabConfig?.types ?? undefined,
        unreadOnly,
        campaignId: campaignId || undefined,
    }

    const { data, loading, error } = useQuery(GET_NOTIFICATIONS, {
        variables: queryVariables,
        skip: !user?.id,
        fetchPolicy: 'cache-and-network',
    })

    const [markNotificationsRead] = useMutation(MARK_NOTIFICATIONS_READ, {
        refetchQueries: [{ query: GET_NOTIFICATIONS, variables: queryVariables }],
    })
    const [markAllNotificationsRead] = useMutation(MARK_ALL_NOTIFICATIONS_READ, {
        refetchQueries: [
            { query: GET_NOTIFICATIONS, variables: queryVariables },
            { query: GET_UNREAD_NOTIFICATIONS_COUNT, variables: { userId: user?.id } },
        ],
    })
    const items = useMemo(() => data?.getNotifications?.items ?? [], [data])

    const handleMarkOneRead = async (notification) => {
        if (!notification.isNew) return
        await markNotificationsRead({ variables: { ids: [notification.id] } })
        decrementUnreadBy(1)
    }

    const handleMarkAllRead = async () => {
        await markAllNotificationsRead({ variables: { userId: user?.id } })
        resetUnread()
        setUnreadCount(0)
    }

    const onClickMarkReadButton = (event) => {
        const id = event.currentTarget.getAttribute('data-target')
        const notification = items.find((n) => n.id === id)
        if (notification) handleMarkOneRead(notification)
    }

    const bindButtonEvents = () => {
        const markReadButtons = document.getElementsByClassName('mark-read-icon')
        Array.from(markReadButtons).forEach((button) => {
            button.removeEventListener('click', onClickMarkReadButton)
            button.addEventListener('click', onClickMarkReadButton)
        })
    }

    const tableData = useMemo(
        () =>
            items.map((notification) => {
                const meta = TYPE_META[notification.type] ?? { title: notification.type, category: 'System' }
                return {
                    notification: { title: meta.title, message: notification.message },
                    status: notification.isNew,
                    type: meta.category,
                    date: formatDate(notification.createdAt),
                    id: { id: notification.id, isNew: notification.isNew },
                }
            }),
        [items],
    )

    const columns = useMemo(
        () => [
        {
            title: 'Notification',
            data: 'notification',
            render: (data) =>
                `<div><div class="small fw-semibold">${escapeHtml(data.title)}</div><div class="small text-muted">${escapeHtml(data.message)}</div></div>`,
        },
        {
            title: 'Status',
            data: 'status',
            render: (isNew) =>
                `<span class="small badge rounded-pill text-white" style="background-color: ${isNew ? STATUS_BADGE_COLOR.unread : STATUS_BADGE_COLOR.read}">${isNew ? 'Unread' : 'Read'}</span>`,
        },
        {
            title: 'Type',
            data: 'type',
            render: (category) =>
                `<span class="small badge rounded-pill text-white" style="background-color: ${CATEGORY_BADGE_COLOR[category] ?? '#6c757d'}">${escapeHtml(category)}</span>`,
        },
        {
            title: 'Date',
            data: 'date',
            render: (date) => `<span class="small">${escapeHtml(date)}</span>`,
        },
        {
            title: 'Actions',
            data: 'id',
            className: 'text-end',
            orderable: false,
            render: (data) => {
                if (!data.isNew) return ''
                return (
                    '<a class="btn btn-icon mark-read-icon opacity-100 tooltip" data-target="' +
                    escapeHtml(data.id) +
                    '"><span class="btn-inner">' +
                    checkIconSvg +
                    '</span><span class="tooltiptext">Mark as read</span></a>'
                )
            },
        },
        ],
        [],
    )

    return (
        <Card className="page-card">
            <Card.Header className="tw:flex tw:items-center tw:justify-between tw:flex-wrap tw:gap-3">
                <div className="header-title d-flex align-items-center justify-content-center">
                    <i className="icon text-gray-200 me-1">
                        <Bell size={24} color="currentColor" />
                    </i>
                    <h5 className="title mb-0 text-white">Notifications</h5>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                    <span className="tw:text-sm tw:text-muted-foreground">License</span>
                    <Select value={campaignId} onValueChange={(value) => setCampaignId(value === 'all' ? '' : value)}>
                        <SelectTrigger className="tw:w-[200px]">
                            <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            {campaigns.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                <div className="tw:flex tw:flex-col tw:px-6 tw:pt-4 tw:border-b">
                    <div className="tw:flex tw:items-end tw:justify-between tw:flex-wrap tw:gap-3">
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList>
                                {TABS.map((tab) => (
                                    <TabsTrigger
                                        key={tab.value}
                                        value={tab.value}
                                        className="tw:text-base tw:font-semibold tw:py-2 tw:data-[state=active]:bg-gray-500 tw:data-[state=active]:border-b-4 tw:data-[state=active]:border-blue-500"
                                    >
                                        {tab.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                        <div className="tw:flex tw:items-center tw:gap-6 tw:mb-4">
                            <Form.Check className="d-flex align-items-center gap-2 m-0">
                                <FormCheck.Input
                                    type="checkbox"
                                    id="unreadOnly"
                                    name="unreadOnly"
                                    checked={unreadOnly}
                                    onChange={(event) => setUnreadOnly(event.target.checked)}
                                ></FormCheck.Input>
                                <Form.Check.Label
                                    htmlFor="unreadOnly"
                                    className="mb-0 tw:text-sm tw:font-semibold tw:text-foreground tw:cursor-pointer"
                                >
                                    Show only unread
                                </Form.Check.Label>
                            </Form.Check>
                            <Button variant="secondary" size="sm" onClick={handleMarkAllRead}>
                                Mark All as Read
                            </Button>
                        </div>
                    </div>
                </div>

                {loading && items.length === 0 ? (
                    <CubeLoader size="default" />
                ) : error ? (
                    <div className="tw:py-10 tw:text-center tw:text-sm tw:text-destructive">
                        Failed to load notifications. Please try again.
                    </div>
                ) : items.length === 0 ? (
                    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:py-16 tw:text-center">
                        <Bell className="tw:size-10 tw:text-gray-300" />
                        <p className="tw:text-base tw:font-semibold tw:text-foreground">No notifications yet</p>
                        <p className="tw:text-sm tw:text-gray-200">
                            You&apos;re all caught up. New notifications will show up here.
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <DataTable
                            data={tableData}
                            className="table-striped notifications-table"
                            columns={columns}
                            onDraw={bindButtonEvents}
                            searching={false}
                            pageLength={25}
                            preserveInputOrder
                        />
                    </div>
                )}
            </Card.Body>
        </Card>
    )
}

export default Notifications
